import React, { useCallback, useEffect, useState } from "react";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import {
	Layout,
	Button,
	Card,
	Table,
	Modal,
	Form,
	Input,
	message,
	Popconfirm,
	Space,
	ConfigProvider,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import AttendanceNavigation from "../attendance/AttendanceNavigation";
import OfficeTeamNavigation from "./OfficeTeamNavigation";
import locale from "antd/locale/ko_KR";
import PageHeader from "../common/PageHeader";

const { Content } = Layout;

const usesAttendanceNavigation = () => {
	try {
		const groups = JSON.parse(localStorage.getItem("user_groups") || "[]");
		return Array.isArray(groups) && groups.includes("office_leave");
	} catch {
		return false;
	}
};

const ManageEmployeeTeams = () => {
	const [teams, setTeams] = useState([]);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [editingTeam, setEditingTeam] = useState(null);
	const [form] = Form.useForm();

	const loadTeams = useCallback(async () => {
		try {
			setLoading(true);
			const res = await useAxios.get("/users/employee-teams/");
			if (res.status === 200) {
				setTeams(res.data);
			}
		} catch {
			message.error("직원 팀 목록을 불러오지 못했습니다.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadTeams();
	}, [loadTeams]);

	const openCreate = () => {
		setEditingTeam(null);
		form.resetFields();
		setModalOpen(true);
	};

	const openEdit = (record) => {
		setEditingTeam(record);
		form.setFieldsValue({ name: record.name });
		setModalOpen(true);
	};

	const handleModalOk = async () => {
		try {
			const values = await form.validateFields();
			if (editingTeam) {
				await useAxios.put(
					`/users/employee-teams/${editingTeam.id}/`,
					{ name: values.name },
				);
				message.success("팀 정보가 수정되었습니다.");
			} else {
				await useAxios.post("/users/employee-teams/", {
					name: values.name,
				});
				message.success("팀이 생성되었습니다.");
			}
			setModalOpen(false);
			loadTeams();
		} catch (err) {
			if (err?.errorFields) return;
			message.error("저장에 실패했습니다.");
		}
	};

	const handleDelete = async (record) => {
		try {
			await useAxios.delete(`/users/employee-teams/${record.id}/`);
			message.success("팀이 삭제되었습니다.");
			loadTeams();
		} catch {
			message.error("삭제에 실패했습니다.");
		}
	};

	const columns = [
		{
			title: "ID",
			dataIndex: "id",
			width: 72,
		},
		{
			title: "팀 이름",
			dataIndex: "name",
		},
		{
			title: "멤버 수",
			dataIndex: "member_count",
			width: 100,
		},
		{
			title: "작업",
			key: "actions",
			width: 200,
			render: (_, record) => (
				<Space>
					<Button type="link" onClick={() => openEdit(record)}>
						수정
					</Button>
					<Popconfirm
						title="이 팀과 소속 멤버 정보를 모두 삭제할까요?"
						okText="삭제"
						cancelText="취소"
						onConfirm={() => handleDelete(record)}>
						<Button type="link" danger>
							삭제
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="직원 팀 관리" />
			<Layout className="member-layout">
				{usesAttendanceNavigation() ? (
					<AttendanceNavigation />
				) : (
					<OfficeTeamNavigation />
				)}
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="page">
							<PageHeader
								title="직원 팀 관리"
								actions={
									<Button
										type="primary"
										icon={<PlusOutlined />}
										onClick={openCreate}>
										팀 추가
									</Button>
								}
							/>
							<Card className="surface-card">
								<Table
									rowKey="id"
									columns={columns}
									dataSource={teams}
									loading={loading}
									pagination={false}
								/>
							</Card>
						</div>
					</Content>
				</Layout>
			</Layout>

			<Modal
				title={editingTeam ? "팀 수정" : "팀 추가"}
				open={modalOpen}
				onOk={handleModalOk}
				onCancel={() => setModalOpen(false)}
				okText="저장"
				cancelText="취소"
				destroyOnClose>
				<Form form={form} layout="vertical">
					<Form.Item
						name="name"
						label="팀 이름"
						rules={[
							{
								required: true,
								message: "팀 이름을 입력하세요",
							},
							{ max: 30, message: "최대 30자" },
						]}>
						<Input placeholder="예: 행정팀" />
					</Form.Item>
				</Form>
			</Modal>
		</ConfigProvider>
	);
};

export default ManageEmployeeTeams;
